import { useTranslation } from 'react-i18next';
import { X, Filter, Search } from 'lucide-react';
import { useConfigReturnStatuses } from '@/hooks/useConfigReturnStatuses';

export interface AnfragenFilterValues {
  search?: string;
  status?: string;
}

interface AnfragenFiltersProps {
  filters: AnfragenFilterValues;
  onFiltersChange: (filters: AnfragenFilterValues) => void;
}

/**
 * Translate a status title. Priority:
 * 1. Custom translations from DB (status.translations[lang])
 * 2. i18n defaults for standard statuses
 * 3. Fallback to original DB title
 */
export function translateStatusTitle(
  dbTitle: string,
  t: (key: string, fallback: string) => string,
  customTranslations?: Record<string, string>,
  lang?: string,
): string {
  if (customTranslations && lang && customTranslations[lang]) {
    return customTranslations[lang];
  }
  return t(`generalSettings.returnStatuses.defaults.${dbTitle}`, dbTitle);
}

export default function AnfragenFilters({
  filters,
  onFiltersChange,
}: AnfragenFiltersProps) {
  const { t, i18n } = useTranslation();
  const { data: statuses } = useConfigReturnStatuses();
  const lang = i18n.language;

  const activeCount = [filters.search, filters.status].filter(Boolean).length;

  const handleReset = () => {
    onFiltersChange({});
  };

  return (
    <div className="card bg-base-200 border border-go-border rounded-card shadow">
      <div className="card-body p-5 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-base-content/50" />
            <span className="font-semibold text-sm">{t('common.filter')}</span>
            {activeCount > 0 && (
              <span className="badge badge-primary badge-sm">{activeCount}</span>
            )}
          </div>
          {activeCount > 0 && (
            <button
              className="btn btn-ghost btn-xs text-base-content/50"
              onClick={handleReset}
              title={t('deliveries.filters.reset')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Search */}
        <div className="form-control">
          <label className="label-uppercase text-xs mb-1.5">
            {t('common.search')}
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-base-content/40 pointer-events-none" />
            <input
              type="text"
              className="input input-bordered input-sm w-full pl-9"
              placeholder={t('anfragen.searchPlaceholder')}
              value={filters.search ?? ''}
              onChange={(e) =>
                onFiltersChange({
                  ...filters,
                  search: e.target.value || undefined,
                })
              }
            />
            {filters.search && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content"
                onClick={() =>
                  onFiltersChange({ ...filters, search: undefined })
                }
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Status Filter */}
        <div className="form-control">
          <label className="label-uppercase text-xs mb-1.5">
            {t('anfragen.status')}
          </label>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.status ?? ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                status: e.target.value || undefined,
              })
            }
          >
            <option value="">{t('anfragen.allStatuses')}</option>
            {statuses?.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {translateStatusTitle(s.title, t, s.translations, lang)}
              </option>
            ))}
          </select>
        </div>

        {/* Reset Button */}
        {activeCount > 0 && (
          <button
            className="btn btn-ghost btn-sm w-full text-base-content/60"
            onClick={handleReset}
          >
            <X className="h-4 w-4" />
            {t('deliveries.filters.reset')}
          </button>
        )}
      </div>
    </div>
  );
}
